import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import { GET_MANAGEABLE_SECTIONS } from "../queries";
import { Section } from "../../../types";

interface SectionsQueryData {
  manageableSections: {
    nodes: Section[];
  };
}

interface SectionAutocompleteProps {
  params: { [key: string]: any };
  addSearchParam: (key: string, value: string) => void;
  deleteSearchParams: (key: string | string[]) => void;
  onSectionFilterChange?: (applied: boolean) => void;
}

const SectionAutocompleteWrapper = ({
  params,
  addSearchParam,
  deleteSearchParams,
  onSectionFilterChange
}: SectionAutocompleteProps) => {
  const { data: sectionData } = useQuery<SectionsQueryData>(GET_MANAGEABLE_SECTIONS, {
    variables: { input: { search: null } }
  });
  const manageableSections = useMemo(() => sectionData?.manageableSections?.nodes || [], [sectionData]);

  useEffect(() => {
    if (manageableSections.length === 1) {
      const [section] = manageableSections;
      addSearchParam("sectionId", section.id);
    }
  }, [manageableSections, addSearchParam]);

  if (manageableSections.length <= 1) {
    return <></>;
  } else {
    return (
      <SectionAutocomplete
        sections={manageableSections}
        params={params}
        addSearchParam={addSearchParam}
        deleteSearchParams={deleteSearchParams}
        onSectionFilterChange={onSectionFilterChange}
      />
    );
  }
};

interface SectionAutocompleteComponentProps extends SectionAutocompleteProps {
  sections: Section[];
}

const SectionAutocomplete = ({
  sections,
  params,
  addSearchParam,
  deleteSearchParams,
  onSectionFilterChange
}: SectionAutocompleteComponentProps) => {
  const [error, setError] = useState<string | undefined>(undefined);

  function selectSection(section: Section) {
    addSearchParam("sectionId", section.id);
    addSearchParam("section", section.name);
    onSectionFilterChange?.(true);
    setError(undefined);
  }

  const filterSections = (value: string) =>
    sections.filter((section) => section.name.toLowerCase().includes(value?.toLowerCase() || ""));

  return (
    <Autocomplete
      variant="searchableDropdown"
      label="Section"
      query={params.section || ""}
      data={filterSections(params.section || "")}
      menuClassName="max-h-[20rem]"
      onChangeText={(newValue) => {
        if (!newValue) {
          const hadSectionId = Boolean(params.sectionId);
          deleteSearchParams(["sectionId", "section"]);
          if (hadSectionId) {
            onSectionFilterChange?.(false);
          }
        } else {
          addSearchParam("section", newValue);
          deleteSearchParams(["sectionId"]);
        }
      }}
      onBlur={() => {
        if (params.section && !params.sectionId) {
          setError("Please select a valid section");
        }
      }}
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault();
      }}
      onSelect={selectSection}
      reset={() => {
        const hadSectionId = Boolean(params.sectionId);
        deleteSearchParams(["sectionId", "section"]);
        if (hadSectionId) {
          onSectionFilterChange?.(false);
        }
        setError(undefined);
      }}
      error={!!error}
      errorText={error}
      renderItem={(data) => (
        <div className="flex items-center border-bottom">
          <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
            <Icon name="domain" size={24} />
          </div>
          <div>
            <div className="text-body-large">{data.name}</div>
          </div>
        </div>
      )}
    />
  );
};

export { SectionAutocomplete };
export default SectionAutocompleteWrapper;
